import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';

interface DescriptionEditorProps {
  /** ラベルテキスト */
  label: string;
  /** 現在の値 */
  value: string;
  /** 編集モードかどうか */
  isEditing: boolean;
  /** 保存中かどうか */
  isSaving: boolean;
  /** Gemini生成テキスト（差分表示用） */
  generatedText?: string;
  /** コンパクト表示（カラム用） */
  compact?: boolean;
  /** 読み取り専用（編集ボタン非表示） */
  readOnly?: boolean;
  /** 編集開始 */
  onEdit: () => void;
  /** 値変更 */
  onChange: (value: string) => void;
  /** 保存 */
  onSave: () => void;
  /** キャンセル */
  onCancel: () => void;
}

/**
 * 説明文の編集コンポーネント
 * Gemini生成テキストと人手レビューテキストの差分を表示
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 説明文エディター。AI生成テキストと人間レビューテキストの差分表示、インライン編集・保存・キャンセル操作を提供
 * @graph-connects none
 */
export function DescriptionEditor({
  label,
  value,
  isEditing,
  isSaving,
  generatedText,
  compact = false,
  readOnly = false,
  onEdit,
  onChange,
  onSave,
  onCancel,
}: DescriptionEditorProps) {
  const { t } = useTranslation();

  // 編集モード
  if (isEditing) {
    return (
      <div className={compact ? 'space-y-2' : 'space-y-3'}>
        {label && <p className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</p>}
        <textarea
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          rows={compact ? 2 : 4}
          className={`
            w-full
            bg-[var(--color-bg-surface)]
            border border-[var(--color-border)] rounded-lg
            text-sm text-[var(--color-text-primary)]
            placeholder:text-[var(--color-text-muted)]
            focus:outline-none focus:ring-2 focus:ring-[var(--color-border-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)]
            resize-y
            ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}
          `}
          placeholder={generatedText ?? ''}
        />
        <div className="flex gap-2">
          <Button variant="primary" size="sm" loading={isSaving} onClick={onSave}>
            {isSaving ? t('dbDictionary.actions.saving') : t('dbDictionary.actions.save')}
          </Button>
          <Button variant="ghost" size="sm" disabled={isSaving} onClick={onCancel}>
            {t('dbDictionary.actions.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  // 表示モード
  const hasHumanDescription = value && value.length > 0;
  const isDifferent = hasHumanDescription && value !== generatedText;

  if (readOnly) {
    return (
      <div className={compact ? 'space-y-1' : 'space-y-2'}>
        {label && <p className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</p>}
        {hasHumanDescription ? (
          <p
            className={`${compact ? 'text-xs' : 'text-sm'} text-[var(--color-text-primary)] whitespace-pre-wrap`}
          >
            {value}
          </p>
        ) : (
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-[var(--color-text-muted)]`}>-</p>
        )}
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      {label && <p className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</p>}
      <div
        className="cursor-pointer group"
        onClick={onEdit}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onEdit();
        }}
        aria-label={t('actions.edit')}
      >
        {hasHumanDescription ? (
          <p
            className={`${compact ? 'text-xs' : 'text-sm'} text-[var(--color-text-primary)] whitespace-pre-wrap`}
          >
            {value}
          </p>
        ) : (
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-[var(--color-text-muted)] italic`}>
            {t('actions.edit')}
          </p>
        )}
        {isDifferent && !compact && (
          <p className="text-xs text-[var(--color-accent-primary)] mt-1 opacity-70">
            {t('dbDictionary.table.generatedDescription')}: {generatedText}
          </p>
        )}
        <span
          className={`
          ${compact ? 'text-[10px]' : 'text-xs'}
          text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity
        `}
        >
          {t('actions.edit')}
        </span>
      </div>
    </div>
  );
}
